import { useCallback, useRef } from 'react';
import './writingAssistant.scss';

interface ResizeHandleProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  currentValue: number;
  ariaLabel: string;
  onResize: (editorWidthPct: number) => void;
}

const MIN_PCT = 30;
const MAX_PCT = 70;
const KEYBOARD_STEP = 5;

export function ResizeHandle({
  containerRef,
  currentValue,
  ariaLabel,
  onResize,
}: ResizeHandleProps) {
  const activeRef = useRef(false);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);
    activeRef.current = true;
    target.classList.add('writing-assistant-container__resize-handle--active');
  }, []);

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!activeRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      onResize(Math.min(MAX_PCT, Math.max(MIN_PCT, pct)));
    },
    [containerRef, onResize]
  );

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    activeRef.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
    e.currentTarget.classList.remove('writing-assistant-container__resize-handle--active');
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        onResize(Math.max(MIN_PCT, currentValue - KEYBOARD_STEP));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        onResize(Math.min(MAX_PCT, currentValue + KEYBOARD_STEP));
      }
    },
    [currentValue, onResize]
  );

  return (
    <div
      className="writing-assistant-container__resize-handle"
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      aria-valuenow={Math.round(currentValue)}
      aria-valuemin={MIN_PCT}
      aria-valuemax={MAX_PCT}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onKeyDown={handleKeyDown}
    />
  );
}
